import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Body1, Body2, Caption1, Spinner, Badge,
  MessageBar, MessageBarBody,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useSpecQuality } from '../../../api/hooks';
import type { SpecScoreRow, SpecScoreCheck } from '../../../api/types';

// FEAT-021 Spec Quality Scorecard. One row per Feature, sorted worst-first.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  tableWrapper: { flex: 1, overflow: 'auto', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  checks: { display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
});

function scoreColor(score: number) {
  if (score >= 4) return 'success' as const;
  if (score >= 2) return 'warning' as const;
  return 'danger' as const;
}

export function SpecQualityPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useSpecQuality(projectId);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Spec Quality Scorecard</Title2>
        <Body1 className={styles.hint}>
          One row per Feature, scored 0–5 against the Definition of Ready. Worst-first so you know what to refine next.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project.</Body1>
      ) : isLoading ? (
        <Spinner label="Scoring features…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.rows.length === 0 ? (
        <Body1>No Features in this project.</Body1>
      ) : (
        <div className={styles.tableWrapper}>
          <Table aria-label="Spec quality per feature" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Score</TableHeaderCell>
                <TableHeaderCell>Title</TableHeaderCell>
                <TableHeaderCell>State</TableHeaderCell>
                <TableHeaderCell>Checks</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r: SpecScoreRow) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Badge appearance="filled" color={scoreColor(r.score)}>{r.score} / 5</Badge>
                  </TableCell>
                  <TableCell>
                    <Body2>{r.title}</Body2>
                    <Caption1>#{r.id}</Caption1>
                  </TableCell>
                  <TableCell><Caption1>{r.state}</Caption1></TableCell>
                  <TableCell>
                    <div className={styles.checks}>
                      {r.checks.map((c: SpecScoreCheck, idx: number) => (
                        <Badge
                          key={idx}
                          appearance={c.passed ? 'filled' : 'outline'}
                          color={c.passed ? 'success' : 'subtle'}
                          title={c.detail ?? c.check}
                        >
                          {c.passed ? '✓' : '✗'} {c.check}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
